import { ROOT_ID, type Scene } from '@flobi/artwork-core';

/** A selected descendant reveals its artboard; loose artwork remains reachable. */
export function selectionScope(scene: Scene, selection: readonly string[]): string | null {
  for (const id of selection) {
    if (!scene.has(id)) continue;
    if (scene.get(id)?.type === 'artboard') return id;
    for (const ancestor of scene.ancestors(id)) if (scene.get(ancestor)?.type === 'artboard') return ancestor;
    return ROOT_ID;
  }
  return null;
}

/** Layer rows read front to back, the reverse of the engine's sibling order. */
export function layerMoveTarget(scene: Scene, id: string, direction: -1 | 1) {
  const parentId = scene.parentOf(id) ?? ROOT_ID;
  const siblings = [...scene.childrenOf(parentId)].reverse();
  const at = siblings.indexOf(id), neighbor = at < 0 ? undefined : siblings[at + direction];
  return neighbor ? direction === -1 ? { parentId, after: neighbor } : { parentId, before: neighbor } : null;
}

/** Artboards use document order, independent of their position on the canvas. */
export function artboardOrder(scene: Scene, id: string, direction: -1 | 1) {
  const boards = scene.childrenOf(ROOT_ID).filter(one => scene.get(one)?.type === 'artboard');
  const at = boards.indexOf(id), neighbor = at < 0 ? undefined : boards[at + direction];
  if (!neighbor) return null;
  boards.splice(at + direction, 0, boards.splice(at, 1)[0]!);
  return boards;
}

/** How a click on a list row picks: the row alone, one more or fewer (⌘/Ctrl), or a run (Shift). */
export type PickMode = 'only' | 'toggle' | 'range';

export function pickModeOf(event: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }): PickMode {
  return event.shiftKey ? 'range' : event.metaKey || event.ctrlKey ? 'toggle' : 'only';
}

/**
 * What a click on `id` leaves picked, as every layer list does it. `rows` is the list as
 * it reads, already narrowed to the rows a run may cover; `anchor` is the row the last
 * plain or ⌘ click landed on, where a Shift run starts. A run keeps what was picked with ⌘.
 */
export function pickInList(selection: readonly string[], rows: readonly string[], id: string, anchor: string | null, mode: PickMode): string[] {
  if (mode === 'toggle') return selection.includes(id) ? selection.filter(one => one !== id) : [...selection, id];
  if (mode === 'range') {
    const from = anchor && rows.includes(anchor) ? anchor : [...selection].reverse().find(one => rows.includes(one));
    const a = from ? rows.indexOf(from) : -1, b = rows.indexOf(id);
    if (a < 0 || b < 0) return [id];
    const run = rows.slice(Math.min(a, b), Math.max(a, b) + 1);
    return [...selection.filter(one => !run.includes(one)), ...run];
  }
  return [id];
}
